import { motion } from 'framer-motion';
import { Outlet, useParams } from 'react-router-dom';

import { SpaceDiscriminator, useGetBuildingsByIdQuery } from '@client/api';
import { PageDetailContent } from '@client/toolkit';

import { useBuildingPackages } from '../../../../../hooks/useBuildingPackages';
import { PortfolioTenantsCard } from '../../../components/portfolio-tenants-card/portfolio-tenants-card.component';
import { SpaceOccupancyCard } from '../../../components/space-occupancy-card/space-occupancy-card.component';

export function SpaceAgreementPartiesRoute() {
  const { buildingId, spaceId, discriminator } = useParams<{
    buildingId: string;
    spaceId: string;
    discriminator: SpaceDiscriminator;
  }>();

  const { data: building } = useGetBuildingsByIdQuery({ id: buildingId ?? '' }, { skip: !buildingId });
  const { isPortfolioOnly } = useBuildingPackages(building?.packages);

  // The agreement-parties card used to sit above this on Management buildings, listing the same
  // counterparty the register now projects as a read-only Leietaker row. It held no data of its
  // own — the agreement is edited on the Avtaler tab — so keeping it only produced two answers to
  // "who is the tenant here": duplicated when the agreement was active, and contradictory when it
  // was not, because that card applied no status filter at all. Removed per §1.

  // The occupancy register lists who *uses* the area. It is not tied to the Portfolio package or
  // to CommercialSpace: an operator can occupy a common area or a parking spot, and if those
  // cannot be recorded the occupier list stays permanently incomplete (§1).
  const showOccupancy = discriminator !== 'Building';

  // Portfolio-only buildings have no agreements, so the manual portfolio-tenant row is the only
  // record of the counterparty *and* of the yearly income that feeds the portfolio-overview
  // report. It therefore stays editable here, standing in for the agreement panel that is
  // hidden on this package. The occupancy register projects it as a read-only Leietaker row, so
  // the two never disagree. Converging the two tables is deliberately deferred.
  const showPortfolioTenants = isPortfolioOnly && discriminator === 'CommercialSpace';

  return (
    <>
      <PageDetailContent align="left">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            opacity: { ease: 'linear', duration: 0.2 },
            y: { ease: 'easeInOut', duration: 0.4 },
          }}
          className="space-y-2"
        >
          {showPortfolioTenants && <PortfolioTenantsCard spaceId={spaceId} buildingId={buildingId} />}
          {showOccupancy && <SpaceOccupancyCard spaceId={spaceId} buildingId={buildingId} />}
        </motion.div>
      </PageDetailContent>
      <Outlet />
    </>
  );
}
